import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type {
  AmendPaidSalesDocumentResponse,
  SalesDocumentDetail,
} from '@aeris/contracts'
import { ZERO_MONEY, add, formatMoney, multiply, subtract } from '@aeris/money'
import { parseAmount, parseQuantity } from '@aeris/sales'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
  notify,
} from '@aeris/ui'

import { ApiError, amendPaidSalesDocument } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { KIND_LABELS } from '../lib/sales-labels.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'

/**
 * Corrigir itens de um pedido ou venda já paga (F348).
 *
 * Antes, tirar uma peça de um pedido acertado era estornar a baixa no
 * Financeiro, cancelar título e editar — e o estorno de uma baixa a prazo
 * deixava a dívida em dobro (pedido 1516). Aqui é uma folha: diz o que sai e
 * se voltou para a loja, e o Aeris acerta o resto. O valor que sai abate o que
 * a venda ainda deve; o que sobrar vira vale do cliente.
 */

type Change = { readonly keep: string; readonly restock: boolean }

const quantityText = (value: string): string =>
  value.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')

const ZERO = ZERO_MONEY

export const SalesPaidAmendDialog = ({
  document,
  onClose,
  onDone,
}: {
  readonly document: SalesDocumentDetail
  readonly onClose: () => void
  readonly onDone: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canAmend = allows('sales.amend')
  const [changes, setChanges] = useState<Record<string, Change>>(() =>
    Object.fromEntries(
      document.lines.map((line) => [
        line.id,
        { keep: quantityText(line.quantity), restock: true },
      ])
    )
  )
  const [reason, setReason] = useState('')
  const [askingManager, setAskingManager] = useState(false)
  const [done, setDone] = useState<AmendPaidSalesDocumentResponse | null>(null)

  const keepOf = (lineId: string, fallback: string) =>
    parseQuantity(changes[lineId]?.keep ?? fallback) ?? null

  // O total novo é uma prévia: o servidor recalcula com o desconto rateado.
  const removedValue = document.lines.reduce((total, line) => {
    const sold = parseQuantity(line.quantity)
    const keep = keepOf(line.id, line.quantity)
    const price = parseAmount(line.unitPrice)
    if (sold === null || keep === null || price === null) return total
    return add(total, multiply(price, subtract(sold, keep)))
  }, ZERO)
  const changed = document.lines.filter((line) => {
    const keep = keepOf(line.id, line.quantity)
    const sold = parseQuantity(line.quantity)
    return keep !== null && sold !== null && subtract(sold, keep).units > 0n
  })
  const invalid = document.lines.some((line) => {
    const keep = keepOf(line.id, line.quantity)
    const sold = parseQuantity(line.quantity)
    return keep === null || sold === null || subtract(sold, keep).units < 0n
  })

  const amend = useMutation({
    mutationFn: async (authorisationId: string | undefined) =>
      await amendPaidSalesDocument(document.id, {
        version: document.version,
        lines: changed.map((line) => ({
          lineId: line.id,
          quantity: changes[line.id]?.keep ?? line.quantity,
          restock: changes[line.id]?.restock ?? true,
        })),
        reason: reason.trim(),
        ...(authorisationId === undefined ? {} : { authorisationId }),
      }),
    onSuccess: async (result) => {
      setDone(result)
      notify.success('Pedido corrigido.')
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
      await queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
  })

  const title = `Corrigir itens — ${KIND_LABELS[document.kind].toLowerCase()}${
    document.number === null ? '' : ` nº ${String(document.number)}`
  }`

  const submit = () => {
    if (canAmend) {
      amend.mutate(undefined)
    } else {
      setAskingManager(true)
    }
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) (done === null ? onClose : onDone)()
        }}
      >
        <DialogContent
          title={title}
          description="Diga o que sai e se voltou para a loja. O valor abate o que este pedido ainda deve; o que sobrar vira vale do cliente."
          className="max-w-3xl"
        >
          {done !== null ? (
            <div className="flex flex-col gap-2 text-(length:--text-sm)">
              <p>
                O total foi de{' '}
                <strong>
                  {formatMoney(parseAmount(done.totalBefore) ?? ZERO)}
                </strong>{' '}
                para{' '}
                <strong>
                  {formatMoney(parseAmount(done.totalAfter) ?? ZERO)}
                </strong>
                .
              </p>
              {done.titles.map((change) => (
                <p key={change.id}>
                  O título de{' '}
                  {new Date(change.dueAt).toLocaleDateString('pt-BR')}{' '}
                  {change.to === null
                    ? 'foi cancelado: o abatimento cobriu tudo.'
                    : `foi de ${formatMoney(parseAmount(change.from) ?? ZERO)} para ${formatMoney(parseAmount(change.to) ?? ZERO)}.`}
                </p>
              ))}
              {(parseAmount(done.voucher) ?? ZERO).units > 0n ? (
                <p>
                  {formatMoney(parseAmount(done.voucher) ?? ZERO)} viraram vale
                  do cliente.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Produto</TableHeaderCell>
                      <TableHeaderCell numeric>Vendido</TableHeaderCell>
                      <TableHeaderCell numeric>Fica</TableHeaderCell>
                      <TableHeaderCell>Voltou para a loja</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {document.lines.map((line) => {
                      const change = changes[line.id]
                      return (
                        <TableRow key={line.id}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell numeric>
                            {quantityText(line.quantity)}
                          </TableCell>
                          <TableCell numeric>
                            <Input
                              aria-label={`Quantidade que fica de ${line.description}`}
                              inputMode="decimal"
                              className="w-20 text-right"
                              value={change?.keep ?? ''}
                              onChange={(event) => {
                                const keep = event.target.value.replace(
                                  ',',
                                  '.'
                                )
                                setChanges((all) => ({
                                  ...all,
                                  [line.id]: {
                                    keep,
                                    restock: all[line.id]?.restock ?? true,
                                  },
                                }))
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              aria-label={`${line.description} voltou para a loja`}
                              checked={change?.restock ?? true}
                              onCheckedChange={(checked) => {
                                setChanges((all) => ({
                                  ...all,
                                  [line.id]: {
                                    keep: all[line.id]?.keep ?? line.quantity,
                                    restock: checked === true,
                                  },
                                }))
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {changed.length > 0 && !invalid ? (
                <p className="text-(length:--text-sm)">
                  Saem cerca de <strong>{formatMoney(removedValue)}</strong>.
                  Primeiro abatem o que este pedido ainda deve; o que sobrar
                  vira vale do cliente.
                </p>
              ) : null}

              <Field label="Por que está corrigindo" required>
                {(field) => (
                  <Textarea
                    {...field}
                    value={reason}
                    maxLength={500}
                    onChange={(event) => {
                      setReason(event.target.value)
                    }}
                  />
                )}
              </Field>

              {invalid ? (
                <InlineAlert tone="danger">
                  Aqui só se tira: a quantidade que fica vai de zero até o que
                  foi vendido.
                </InlineAlert>
              ) : null}
              {amend.isError ? (
                <InlineAlert tone="danger">
                  {amend.error instanceof ApiError
                    ? amend.error.message
                    : 'Não foi possível corrigir. Tente de novo.'}
                </InlineAlert>
              ) : null}
            </div>
          )}

          <DialogFooter>
            {done !== null ? (
              <Button variant="primary" onClick={onDone}>
                Fechar
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  loading={amend.isPending}
                  disabled={
                    changed.length === 0 || invalid || reason.trim().length < 3
                  }
                  onClick={submit}
                >
                  Corrigir
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {askingManager ? (
        <AuthorisationDialog
          title={title}
          description="Corrigir um pedido que já saiu pede o e-mail e a senha do gerente, do admin ou do dono."
          action="sales.amend"
          targetType="sales_document"
          targetId={document.id}
          fixedReason={reason.trim()}
          onAuthorised={(authorisationId) => {
            setAskingManager(false)
            amend.mutate(authorisationId)
          }}
          onClose={() => {
            setAskingManager(false)
          }}
        />
      ) : null}
    </>
  )
}
